import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useAuthStore, selectIsAuthenticated } from '@/stores/authStore'
import { startGoogleLogin, startFacebookLogin } from '@/services/api'
import { useAuthProviders } from '@/hooks/useAuthProviders'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BrandLogo } from '@/components/low/BrandLogo'
import { QrLoginPanel } from '@/components/high/auth/QrLoginPanel'
import { isTvScreen } from '@/lib/device'

export function LoginPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const returnUrl = searchParams.get('returnUrl') || '/'

    const isAuthenticated = useAuthStore(selectIsAuthenticated)
    const isInitialized = useAuthStore((s) => s.isInitialized)
    const { login, isLoading } = useAuthStore()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [serverError, setServerError] = useState<string | null>(null)

    const providers = useAuthProviders()
    const hasSocialLogin = providers.google || providers.facebook

    // Same threshold HostPage uses to treat the screen as a TV (display_only)
    // Evaluated once: a resize must not remount QrLoginPanel and invalidate a scanned code
    const [isTv] = useState(isTvScreen)

    // Redirect if already logged in
    useEffect(() => {
        if (isInitialized && isAuthenticated) {
            navigate(returnUrl, { replace: true })
        }
    }, [isInitialized, isAuthenticated, navigate, returnUrl])

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setServerError(null)
        try {
            await login(email, password)
            navigate(returnUrl, { replace: true })
        } catch (err) {
            const msg = (err as { message?: string }).message || t('auth.loginFailed')
            setServerError(msg)
            toast.error(msg)
        }
    }

    const handleGoogleLogin = () => {
        sessionStorage.setItem('auth-return-url', returnUrl)
        startGoogleLogin()
    }

    const handleFacebookLogin = () => {
        sessionStorage.setItem('auth-return-url', returnUrl)
        startFacebookLogin()
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className={`${isTv ? 'max-w-5xl' : 'max-w-2xl'} mx-auto px-4 py-8 md:px-6 md:py-12`}>
                {/* Header */}
                <header className="mb-8">
                    <Link to="/" className="flex items-center gap-3 hover:opacity-80 w-fit mb-6">
                        <BrandLogo className="h-8 md:h-9" />
                    </Link>
                    <h1 className="text-xl font-display font-semibold text-foreground">{t('auth.login')}</h1>
                </header>

                {/* Content */}
                <main className={isTv ? 'grid grid-cols-2 gap-6 items-start' : undefined}>
                    <Card>
                        <CardHeader className="mb-0" />
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Email */}
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('auth.emailLabel')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isLoading}
                                        placeholder={t('auth.emailPlaceholder')}
                                        autoComplete="email"
                                        required
                                        className="min-h-[44px]"
                                    />
                                </div>

                                {/* Password */}
                                <div className="space-y-2">
                                    <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={isLoading}
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        required
                                        className="min-h-[44px]"
                                    />
                                </div>

                                {/* Server error */}
                                {serverError && (
                                    <p
                                        data-testid="login-error"
                                        className="text-destructive text-sm text-center"
                                    >
                                        {serverError}
                                    </p>
                                )}

                                {/* Submit */}
                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full min-h-[44px]"
                                >
                                    {isLoading ? t('auth.signingIn') : t('auth.login')}
                                </Button>

                                {hasSocialLogin && (
                                    <>
                                {/* OAuth Divider */}
                                <div className="flex items-center gap-4">
                                    <div className="h-px flex-grow bg-border" />
                                    <span className="text-xs text-muted-foreground">{t('auth.or')}</span>
                                    <div className="h-px flex-grow bg-border" />
                                </div>

                                {/* OAuth Buttons - side by side */}
                                <div className={providers.google && providers.facebook ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 gap-3'}>
                                    {providers.google && (
                                    <Button
                                        type="button"
                                        onClick={handleGoogleLogin}
                                        disabled={isLoading}
                                        variant="outline"
                                        className="min-h-[44px] flex items-center justify-center gap-2"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            width="18"
                                            height="18"
                                            aria-hidden="true"
                                        >
                                            <path
                                                fill="#4285F4"
                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            />
                                            <path
                                                fill="#34A853"
                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            />
                                            <path
                                                fill="#FBBC05"
                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                                            />
                                            <path
                                                fill="#EA4335"
                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            />
                                        </svg>
                                        Google
                                    </Button>
                                    )}
                                    {providers.facebook && (
                                    <Button
                                        type="button"
                                        onClick={handleFacebookLogin}
                                        disabled={isLoading}
                                        variant="outline"
                                        className="min-h-[44px] flex items-center justify-center gap-2"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            width="18"
                                            height="18"
                                            aria-hidden="true"
                                        >
                                            <path
                                                fill="#1877F2"
                                                d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.532-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"
                                            />
                                        </svg>
                                        Facebook
                                    </Button>
                                    )}
                                </div>
                                    </>
                                )}

                                {/* Register link */}
                                <p className="text-center text-sm text-muted-foreground">
                                    {t('auth.noAccount')}{' '}
                                    <Link
                                        to={`/register${returnUrl !== '/' ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`}
                                        className="text-primary hover:text-primary-hover transition-colors font-semibold"
                                    >
                                        {t('auth.register')}
                                    </Link>
                                </p>
                            </form>
                        </CardContent>
                    </Card>
                    {isTv && <QrLoginPanel />}
                </main>
            </div>
        </div>
    )
}
